<!-- eslint-disable vue/valid-template-root -->
<template></template>

<script setup lang="ts">
import { computed, watch, onMounted, onUnmounted, defineAsyncComponent } from 'vue';
import { useRoute, useNuxtApp } from '#imports';
import { openModal } from '~/composables/modal';
import { type ModalOpenProps } from '@i22/td-longtail-vue-components';

const excludedRoutes = ['/shop', '/handyrechnung-aktion', '/magenta-moments-2024'];
const excludedSubroutes = ['/maintenance', '/redirecting', '/smart-home'];

const includedRoutes = ['/', '/preisvorteile-sichern'];

const route = useRoute();
const { $store } = useNuxtApp();

const dateToday = computed(() => new Date($store.getters['server-date/getServerDate']).getTime());

const storage =
  typeof navigator !== 'undefined' && typeof localStorage !== 'undefined' && localStorage !== null
    ? localStorage
    : undefined;

type Popup = ModalOpenProps & {
  className?: string; // 'default-popup--fullsize' | 'default-popup--no-header';
  closeOnBackdrop: true;
  startDate?: Date; // new Date('2025-04-16 12:16:00')
  endDate?: Date; // new Date('2025-04-16 12:16:00')
};

/**
 * To test it in the Browser, you can set the server-date:
 * $nuxt.$store.state['server-date'].date = new Date('2025-04-17 00:00:00');
 */

const popUps: Popup[] = [
  {
    startDate: new Date('2025-04-25 08:00:00'),
    endDate: new Date('2025-05-18 00:00:00'),
    component: {
      is: defineAsyncComponent(() => import('~/components/default-popup-content.vue')),
      props: {},
    },
    closeOnBackdrop: true,
    className: 'default-popup--fullsize default-popup--no-header',
    buttons: [
      {
        name: 'Zum Angebot',
        tag: 'nuxt-link',
        to: '/geraete/apple-airpods-pro-2-generation-mit-magsafe-ladecase-usb-c-weiss',
        primary: true,
        autofocus: true,
      },
      {
        name: 'Schließen',
        tag: 'button',
        outline: true,
        click: (closeModal) => {
          closeModal();
        },
      },
    ],
  },
];

const currentPopUp = computed<Popup | undefined>(() =>
  popUps.find((p) => {
    if (p.startDate && p.startDate.getTime() > dateToday.value) return false;
    if (p.endDate && p.endDate.getTime() < dateToday.value) return false;
    return true;
  })
);

const openPopup = () => {
  if (!currentPopUp.value) return;

  openModal(currentPopUp.value);
};

const checkPopup = () => {
  setTimeout(() => {
    if (!currentPopUp.value) return;

    // Check, if a dialog is currently open
    if (document.querySelector('dialog')?.open) return;

    if (includedRoutes.length > 0) {
      if (!includedRoutes.includes(route.path)) {
        return;
      }
    } else if (
      excludedRoutes.some((routeEntry: string) => route.path.startsWith(routeEntry)) ||
      excludedSubroutes.includes(route.path)
    ) {
      return;
    }

    // Cookie Consent Modal is open
    if (window?.utag?.gdpr?.getConsentState?.() === 0) return;

    if (!storage) return;

    const todayDateString = new Date().toLocaleDateString();

    const lastShownDate = storage.getItem('POPUP_DATE');
    if (lastShownDate !== todayDateString) {
      openPopup();

      storage.setItem('POPUP_DATE', todayDateString);
    }
  }, 1000);
};

const unwatch = watch(
  route,
  () => {
    checkPopup();
  },
  {
    deep: true,
  }
);

onUnmounted(() => {
  unwatch();
});

onMounted(() => {
  checkPopup();
});
</script>

<style lang="scss">
@use 'assets/base' as *;
.default-popup {
  &--fullsize {
    .base-dialog__body {
      padding-inline: 0;
    }
  }

  &--no-header {
    .base-dialog__header {
      display: none;
    }
  }
}
</style>
